import React, { useEffect } from 'react'
import { CancelIcon, DeleteIcon } from '@/assets/icons'
import {
	CloseButton,
	DeleteButton,
	ModalBackdrop,
	ModalBody,
	ModalContainer,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from './Modal.styles'

export { DeleteButton } from './Modal.styles'

interface ModalProps {
	isOpen: boolean
	onClose: () => void
	title: string
	children: React.ReactNode
	footer?: React.ReactNode
	showCloseButton?: boolean
	size?: 'small' | 'medium' | 'large'
	closeOnBackdropClick?: boolean
	backgroundColor?: string
	closeButtonColor?: string
	onDelete?: () => void
}

const Modal: React.FC<ModalProps> = ({
	isOpen,
	onClose,
	title,
	children,
	footer,
	showCloseButton = true,
	size = 'medium',
	closeOnBackdropClick = true,
	backgroundColor,
	closeButtonColor,
	onDelete,
}) => {
	// Handle ESC key press
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isOpen) {
				onClose()
			}
		}

		if (isOpen) {
			document.addEventListener('keydown', handleEscape)
			// Prevent body scroll when modal is open
			document.body.style.overflow = 'hidden'
		}

		return () => {
			document.removeEventListener('keydown', handleEscape)
			document.body.style.overflow = 'unset'
		}
	}, [isOpen, onClose])

	const handleBackdropClick = (e: React.MouseEvent) => {
		if (closeOnBackdropClick && e.target === e.currentTarget) {
			onClose()
		}
	}

	if (!isOpen) return null

	return (
		<ModalBackdrop onClick={handleBackdropClick}>
			<ModalContainer size={size} backgroundColor={backgroundColor}>
				<ModalHeader>
					<ModalTitle>{title}</ModalTitle>
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						{onDelete && (
							<DeleteButton onClick={onDelete} title="Delete">
								<DeleteIcon width={24} height={24} fill="currentColor" />
							</DeleteButton>
						)}
						{showCloseButton && (
							<CloseButton onClick={onClose} color={closeButtonColor}>
								<CancelIcon width={24} height={24} fill="currentColor" />
							</CloseButton>
						)}
					</div>
				</ModalHeader>
				<ModalBody>{children}</ModalBody>
				{footer && <ModalFooter>{footer}</ModalFooter>}
			</ModalContainer>
		</ModalBackdrop>
	)
}

export default Modal
